interface AnomalyResult {
  isAnomaly: boolean;
  riskScore: number;
  detectedPatterns: string[];
  sanitizedInput: string;
}

const SQL_INJECTION_PATTERNS = [
  /(\b(select|insert|update|delete|drop|create|alter|truncate|exec|execute)\b)/i,
  /(\bunion\s+(all\s+)?select\b)/i,
  /(--|\#|\/\*|\*\/)/,
  /(\bor\b\s+\d+\s*=\s*\d+)/i,
  /(\band\b\s+\d+\s*=\s*\d+)/i,
  /('(\s*)or(\s*)')/i,
  /(;\s*(drop|delete|truncate|update|insert))/i,
  /(\bwaitfor\s+delay\b)/i,
  /(\bbenchmark\s*\()/i,
  /(\bsleep\s*\()/i,
  /(\bchar\s*\(\d+\))/i,
  /(\bconcat\s*\()/i,
  /(\bload_file\s*\()/i,
  /(\binto\s+(out|dump)file\b)/i,
];

const XSS_PATTERNS = [
  /<script[\s\S]*?>[\s\S]*?<\/script>/i,
  /<script[\s\S]*?>/i,
  /javascript\s*:/i,
  /on\w+\s*=/i,
  /<iframe[\s\S]*?>/i,
  /<object[\s\S]*?>/i,
  /<embed[\s\S]*?>/i,
  /<svg[\s\S]*?onload/i,
  /<img[\s\S]*?onerror/i,
  /expression\s*\(/i,
  /url\s*\(\s*['"]?\s*javascript/i,
  /<link[\s\S]*?href\s*=\s*['"]?javascript/i,
  /\beval\s*\(/i,
  /\bdocument\s*\.\s*(cookie|write|location)/i,
  /\bwindow\s*\.\s*location/i,
];

const PATH_TRAVERSAL_PATTERNS = [
  /\.\.\//,
  /\.\.\\/,
  /%2e%2e%2f/i,
  /%2e%2e\//i,
  /\.\.%2f/i,
  /%2e%2e%5c/i,
  /\.\.%5c/i,
  /\.\.\%c0%af/i,
  /\.\.\%c1%9c/i,
  /\/etc\/passwd/i,
  /\/windows\/system32/i,
  /c:\\windows/i,
];

const COMMAND_INJECTION_PATTERNS = [
  /[;&|`$]/,
  /\$\(/,
  /\$\{/,
  /`[^`]+`/,
  /\|\s*\w+/,
  /;\s*\w+/,
  /&&\s*\w+/,
  /\|\|\s*\w+/,
  />\s*\/\w+/,
  /<\s*\/\w+/,
  /\bnc\s+-/i,
  /\bwget\s+/i,
  /\bcurl\s+/i,
  /\bbash\s+-/i,
  /\/bin\/sh/i,
  /\/bin\/bash/i,
];

const SCANNER_PATTERNS = [
  /wp-admin/i,
  /wp-login/i,
  /wp-content/i,
  /phpmyadmin/i,
  /phpinfo/i,
  /\.env/i,
  /\.git/i,
  /\.svn/i,
  /\.htaccess/i,
  /web\.config/i,
  /config\.php/i,
  /database\.yml/i,
  /credentials/i,
  /\.bak$/i,
  /\.sql$/i,
  /\.dump$/i,
  /admin\.php/i,
  /shell\.php/i,
  /c99\.php/i,
  /r57\.php/i,
];

export function analyzeInput(input: string): AnomalyResult {
  const detectedPatterns: string[] = [];
  let riskScore = 0;

  for (const pattern of SQL_INJECTION_PATTERNS) {
    if (pattern.test(input)) {
      detectedPatterns.push('sql_injection');
      riskScore += 40;
      break;
    }
  }

  for (const pattern of XSS_PATTERNS) {
    if (pattern.test(input)) {
      detectedPatterns.push('xss_attempt');
      riskScore += 35;
      break;
    }
  }

  for (const pattern of PATH_TRAVERSAL_PATTERNS) {
    if (pattern.test(input)) {
      detectedPatterns.push('path_traversal');
      riskScore += 30;
      break;
    }
  }

  for (const pattern of COMMAND_INJECTION_PATTERNS) {
    if (pattern.test(input)) {
      detectedPatterns.push('command_injection');
      riskScore += 45;
      break;
    }
  }

  return {
    isAnomaly: riskScore > 0,
    riskScore: Math.min(riskScore, 100),
    detectedPatterns,
    sanitizedInput: sanitizeInput(input),
  };
}

export function analyzePath(path: string): AnomalyResult {
  const detectedPatterns: string[] = [];
  let riskScore = 0;

  for (const pattern of SCANNER_PATTERNS) {
    if (pattern.test(path)) {
      detectedPatterns.push('scanner_path');
      riskScore += 25;
      break;
    }
  }

  for (const pattern of PATH_TRAVERSAL_PATTERNS) {
    if (pattern.test(path)) {
      detectedPatterns.push('path_traversal');
      riskScore += 30;
      break;
    }
  }

  return {
    isAnomaly: riskScore > 0,
    riskScore: Math.min(riskScore, 100),
    detectedPatterns,
    sanitizedInput: path,
  };
}

export function sanitizeInput(input: string): string {
  return input
    .replace(/[<>]/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '')
    .slice(0, 500);
}

export function getInputRiskLevel(riskScore: number): 'low' | 'medium' | 'high' | 'critical' {
  if (riskScore >= 70) return 'critical';
  if (riskScore >= 50) return 'high';
  if (riskScore >= 25) return 'medium';
  return 'low';
}
